/**
 * "Has the lesson's overview landed yet?" - shared by the My Learning card and
 * the lesson page's title.
 *
 * The backend seeds a freshly-created lesson with a default title and an empty
 * description, and the overview task fills in the real ones. So real content
 * means either a non-default title or a non-empty description.
 */

/** The title the backend seeds before the overview task writes a real one. */
export const DEFAULT_LESSON_TITLE = 'My Lesson';

interface OverviewFields {
  title?: string | null;
  description?: string | null;
}

export function hasOverview(lesson: OverviewFields): boolean {
  return (
    Boolean(lesson.description?.trim()) ||
    (!!lesson.title && lesson.title !== DEFAULT_LESSON_TITLE)
  );
}

/**
 * The title to show while a lesson may still be building: the generated title
 * once the overview exists, otherwise what the user typed (`user_prompt`)
 * rather than the placeholder "My Lesson". Undefined while nothing is known,
 * so the caller can render its own neutral placeholder.
 */
export function displayLessonTitle(
  lesson: (OverviewFields & { user_prompt?: string | null }) | undefined,
  /** The `overview` step's state from pipeline-status. */
  overviewState: string | undefined
): string | undefined {
  if (!lesson) return undefined;
  if (overviewState === 'completed' || hasOverview(lesson)) {
    return lesson.title ?? undefined;
  }
  return lesson.user_prompt?.trim() || lesson.title || undefined;
}
